import type { RadioGroupProps } from '@mui/material/RadioGroup';
import { useCallback, useMemo } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { RadioField, type RadioFieldProps } from './RadioField.tsx';
import type { RadioOptionType } from './types.ts';

export type RHFRadioFieldProps<T extends FieldValues = FieldValues> = Omit<
  RadioFieldProps,
  'name' | 'options' | 'value' | 'defaultValue'
> & {
  control?: Control<T>;
  name: FieldPath<T>;
  options: RadioOptionType<unknown>[];
  onChangeComplete?: (value: unknown) => void;
};

export const RHFRadioField = <T extends FieldValues>({
  control,
  name,
  options,
  onChangeComplete,
  ...rest
}: RHFRadioFieldProps<T>) => {
  const {
    field: { ref, ...field },
    fieldState,
    formState: { isSubmitting },
  } = useController<T, typeof name>({
    control,
    name,
  });

  const stringValuedOptions = useMemo(
    () => options.map(({ value, ...option }) => ({ ...option, value: JSON.stringify(value) })),
    [options]
  );

  const isDisabled = isSubmitting || rest.isDisabled;
  const isError = fieldState.invalid ?? rest.isError;
  const helperText = fieldState.error?.message ?? rest.helperText;

  const handleChange = useCallback<Required<RadioGroupProps>['onChange']>(
    (event, value) => {
      const parsedValue = JSON.parse(value);

      rest.onChange?.(event, parsedValue);
      if (event.isDefaultPrevented()) {
        return;
      }

      field.onChange(parsedValue);
      onChangeComplete?.(parsedValue);
    },
    [rest.onChange, field.onChange, onChangeComplete]
  );

  return (
    <RadioField
      {...rest}
      {...field}
      fieldRef={ref}
      helperText={helperText}
      isDisabled={isDisabled}
      isError={isError}
      onChange={handleChange}
      options={stringValuedOptions}
      /* RHF, under the hood, uses component in "controlled" mode.
      This prevents failing if form start's with undefined as the value
      (for optional fields) or fields without explicit initial value */
      value={JSON.stringify(field.value) || ''}
    />
  );
};
